import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../redux/root-reducer';
import type { ICampaignParticipantDetail } from '../../../redux/buzzbridge/types';
import { rescindCampaignParticipantApproval } from '../../../redux/buzzbridge/actions';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';

const QUICK_REASONS = [
    'Aesthetic Mismatch',
    'Budget Constraint',
    'Content Style',
    'Audience Location',
    'Niche Overlap',
] as const;

const SUBMITTED_CONTENT_STATUSES = new Set([
    'submitted',
    'needs_revision',
    'approved',
    'shared',
    'in review',
    'delivered',
]);

export function participantHasSubmission(creator: ICampaignParticipantDetail | null | undefined): boolean {
    if (!creator) return false;
    const status = String(creator.status || '').toLowerCase();
    if (status === 'in_progress') return true;
    const content = String(creator.contentStatus || '')
        .toLowerCase()
        .trim();
    if (!content) return false;
    if (SUBMITTED_CONTENT_STATUSES.has(content)) return true;
    // Labels like "Content Pending" are not submitted yet.
    return false;
}

export interface RescindApprovalDialogProps {
    open: boolean;
    onClose: () => void;
    creator: ICampaignParticipantDetail | null;
    campaignId: string;
    /** Campaign requirements — shown when rejecting someone who already submitted. */
    requirements?: string[];
}

/** Reject an already-accepted creator (PATCH …/rescind-approval). */
export function RescindApprovalDialog({
    open,
    onClose,
    creator,
    campaignId,
    requirements = [],
}: RescindApprovalDialogProps) {
    const dispatch = useDispatch();
    const rejecting = useSelector((s: RootState) => s.buzzbridge.rejectingRequest);
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [comments, setComments] = useState('');

    const requirementOptions = useMemo(
        () => (requirements || []).map(line => String(line || '').trim()).filter(Boolean),
        [requirements],
    );
    const hasSubmitted = participantHasSubmission(creator);
    const mustPickRequirements = hasSubmitted && requirementOptions.length > 0;
    const pickedRequirements = requirementOptions.filter(line => selectedReasons.includes(line));

    useEffect(() => {
        if (!open) return;
        setSelectedReasons([]);
        setComments('');
    }, [open, creator?._id]);

    const toggleReason = (label: string) => {
        setSelectedReasons(prev => (prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]));
    };

    const reasonReady = mustPickRequirements
        ? pickedRequirements.length > 0
        : hasSubmitted
        ? Boolean(comments.trim()) || selectedReasons.length > 0
        : true;

    const handleConfirm = () => {
        if (!creator || rejecting || !reasonReady) return;
        const parts: string[] = [];
        if (mustPickRequirements && pickedRequirements.length) {
            parts.push(`Missed requirements:\n${pickedRequirements.join('\n')}`);
        } else if (selectedReasons.length) {
            parts.push(`Quick reasons: ${selectedReasons.join(', ')}`);
        }
        if (comments.trim()) parts.push(comments.trim());
        dispatch(
            rescindCampaignParticipantApproval({
                participantId: creator._id,
                campaignId,
                reason: parts.join('\n\n') || undefined,
            }),
        );
        onClose();
    };

    if (!creator) return null;

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!rejecting) onClose();
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                    overflow: 'hidden',
                },
            }}>
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', mb: 0.5 }}>
                    Reject {creator.name}?
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                    {hasSubmitted
                        ? 'This creator already submitted work. Select what was missed and/or leave a reason before rejecting — they’ll be emailed.'
                        : 'This removes them from the accepted roster and frees a spot. They’ll be emailed if a reason is included.'}
                </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {mustPickRequirements ? (
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                mb: 1.5,
                            }}>
                            Missed requirements
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {requirementOptions.map(line => (
                                <FormControlLabel
                                    key={line}
                                    control={
                                        <Checkbox
                                            checked={selectedReasons.includes(line)}
                                            onChange={() => toggleReason(line)}
                                            disabled={rejecting}
                                            size="small"
                                            sx={{
                                                color: 'rgba(255,255,255,0.3)',
                                                '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: 'rgba(255,255,255,0.82)',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            {line}
                                        </Typography>
                                    }
                                />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                mb: 1.5,
                            }}>
                            Quick reasons
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {QUICK_REASONS.map(label => {
                                const on = selectedReasons.includes(label);
                                return (
                                    <Button
                                        key={label}
                                        onClick={() => toggleReason(label)}
                                        sx={{
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            border: on
                                                ? '1px solid rgba(255,201,101,0.45)'
                                                : `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                                            bgcolor: on ? 'rgba(255,201,101,0.1)' : BRAND_SURFACE_CONTAINER_HIGHEST,
                                            color: on ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.55)',
                                            '&:hover': {
                                                borderColor: 'rgba(255,201,101,0.35)',
                                                bgcolor: on ? 'rgba(255,201,101,0.14)' : 'rgba(255,201,101,0.05)',
                                            },
                                        }}>
                                        {label}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.55)',
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            mb: 1.5,
                        }}>
                        {hasSubmitted && !mustPickRequirements ? 'Reason (required)' : 'Message to creator'}
                    </Typography>
                    <TextField
                        multiline
                        fullWidth
                        minRows={4}
                        placeholder={
                            hasSubmitted
                                ? 'Explain why you are rejecting this submission…'
                                : 'Optional details included in the rejection email...'
                        }
                        value={comments}
                        onChange={e => setComments(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                borderRadius: 2,
                                color: '#fff',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: '1px solid rgba(236,170,0,0.35)' },
                            },
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    gap: 1.5,
                    borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}22`,
                    bgcolor: BRAND_SURFACE_CONTAINER,
                }}>
                <Button
                    fullWidth
                    onClick={onClose}
                    disabled={rejecting}
                    sx={{
                        py: 1.25,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.7)',
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                    }}>
                    Cancel
                </Button>
                <Button
                    fullWidth
                    onClick={handleConfirm}
                    disabled={rejecting || !reasonReady}
                    sx={{
                        py: 1.25,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        bgcolor: '#ff6b6b',
                        color: '#fff',
                        '&:hover': { bgcolor: '#ff5252' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,107,107,0.4)', color: 'rgba(255,255,255,0.5)' },
                    }}>
                    {rejecting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Reject'}
                </Button>
            </Box>
        </Dialog>
    );
}
